import React, { FunctionComponent, useEffect, ReactNode, useState, useMemo, useCallback, useRef } from 'react';

// OpenLayers
import OlMap from "ol/Map";
import OlFeature from "ol/Feature";
import OlSourceVector from "ol/source/Vector";
import OlPoint from "ol/geom/Point";
import OlLayerVector from "ol/layer/Vector";
import { LineString as OlLineString } from "ol/geom";
import { getLength as OlSphereLength } from "ol/sphere";
import { transform as OlTransform } from "ol/proj";

// Custom components
import MapRoutingContext from './MapRoutingContext';
import { RouteData, RoutePoint } from '@/components/MapRouting/useRouting';
import {
  // drawingRouteStyle,
  // drawRouteStyle,
  routeStyle,
  routePinPointStyle,
  routeDashedLineStyle,
  originalClickPointStyle,
  routePointStyle,
} from "@/components/Map/mapStyles";

// Types
export interface IMapRoutingProvider {
  children: ReactNode
}

const MapRoutingProvider: FunctionComponent<IMapRoutingProvider> = (props) => {

  const [sharedRouteData, setSharedRouteData] = useState<RouteData | null>(null);
  const [sharedRoutingPoints, setSharedRoutingPoints] = useState<RoutePoint[]>([]);

  // tocke kako ih je vratilo rutiranje
  const clickPointsSourceRef = useRef<OlSourceVector<OlFeature<OlPoint>>>(new OlSourceVector({}));

  // tocke koje je vratilo rutiranje (tocke na stazi najblize kliknutom)
  const routePointsSourceRef = useRef<OlSourceVector<OlFeature<OlPoint>>>(new OlSourceVector({}));

  // ruta
  const routeSourceRef = useRef<OlSourceVector<OlFeature<OlLineString>>>(new OlSourceVector({}));

  // linije koje povezuju tocke gdje smo kliknuli s tockama na stazi od kuda krece rutiranje
  const clickPoint2routePointSourceRef = useRef<OlSourceVector<OlFeature<OlLineString>>>(new OlSourceVector({}));

  // marker koji pokazuje lokaciju s grafa na ruti
  const pointOnRouteSourceRef = useRef<OlSourceVector<OlFeature<OlPoint>>>(new OlSourceVector({}));


  const [clickPointLayer, setClickPointLayer] =
    useState<OlLayerVector<OlSourceVector<OlFeature<OlPoint>>>>(
      new OlLayerVector({
        source: clickPointsSourceRef.current,
        style: originalClickPointStyle,
      })
    );

  const [routePointLayer, setRoutePointLayer] =
    useState<OlLayerVector<OlSourceVector<OlFeature<OlPoint>>>>(
      new OlLayerVector({
        source: routePointsSourceRef.current,
        style: routePinPointStyle,
      })
    );

  const [routeLayer, setRouteLayer] =
    useState<OlLayerVector<OlSourceVector<OlFeature<OlLineString>>>>(
      new OlLayerVector({
        source: routeSourceRef.current,
        style: routeStyle,
        zIndex: 600,
      })
    );

  const [clickPoint2routePointLayer, setClickPoint2RoutePointLayer] =
    useState<OlLayerVector<OlSourceVector<OlFeature<OlLineString>>>>(
      new OlLayerVector({
        source: clickPoint2routePointSourceRef.current,
        style: routeDashedLineStyle,
      })
    );

  const [pointOnRouteLayer, setPointOnRouteLayer] = useState<OlLayerVector<OlSourceVector<OlFeature<OlPoint>>>>(
    new OlLayerVector({
      source: pointOnRouteSourceRef.current,
      style: routePointStyle,
    })
  );

  const { children } = props;

  const setRouteData = useCallback((rd: RouteData | null) => {
    setSharedRouteData(rd);
  }, []);

  const setRoutingPoints = useCallback((rp: RoutePoint[]) => {
    setSharedRoutingPoints(rp);
  }, []);

useEffect(() => {

  let features: OlFeature<OlPoint>[] = [];

  if (sharedRoutingPoints && sharedRoutingPoints.length > 0) {
    features = routePointsSourceRef.current.getFeatures().map((point, index) => {
      const geometry = point.getGeometry() as OlPoint;
      const coordinates = geometry.getCoordinates();
      return new OlFeature<OlPoint>({
        geometry: new OlPoint(coordinates),
        index: index,
      });
    });
  }

  const tolerance = 10; 
  const groups: OlFeature<OlPoint>[][] = [];

  features.forEach(f => {
    const [x, y] = f.getGeometry()!.getCoordinates();
    let foundGroup = false;
    for (const group of groups) {
      const [gx, gy] = group[0].getGeometry()!.getCoordinates();
      const dx = x - gx;
      const dy = y - gy;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance <= tolerance) {
        group.push(f);
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      groups.push([f]);
    }
  });

  groups.forEach(group => {
    const size = group.length;
    group.forEach((f, i) => {
      f.set("clusterIndex", i);
      f.set("clusterSize", size);
    });
  });

  routePointsSourceRef.current.clear();
  routePointsSourceRef.current.addFeatures(features);

}, [sharedRoutingPoints, sharedRouteData]);



  const addLayersToMap = (map: OlMap) => {
    clickPointLayer.setMap(map);
    routePointLayer.setMap(map);
    routeLayer.setMap(map);
    clickPoint2routePointLayer.setMap(map);
    pointOnRouteLayer.setMap(map);
  }


  const providerValue = useMemo(() => ({
    routeData: sharedRouteData,
    setRouteData,
    routingPoints: sharedRoutingPoints,
    setRoutingPoints,
    addLayersToMap,
    clickPointsSourceRef,
    routePointsSourceRef,
    routeSourceRef,
    clickPoint2routePointSourceRef,
    pointOnRouteSourceRef
  }), [sharedRouteData, setRouteData, sharedRoutingPoints, setRoutingPoints]);

  return (
    <MapRoutingContext.Provider value={providerValue}>
      {children}
    </MapRoutingContext.Provider>
  );
};

export default MapRoutingProvider;

